import { useCallback, useEffect, useMemo } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link } from '@tanstack/react-router';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { useIxcRegistrationV1ProfileQueryServiceProfileGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-query-service-profile-get';
import { useIxcRegistrationV1ProfileVersionQueryServiceProfileVersionGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-version-query-service-profile-version-get';
import { FormLayout } from '@interxfi/app-components/components/layout/action-layout/form-layout/form-layout';
import { DEFAULT_PAGE_LAYOUT_GAP } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { TableOfContentsContextProvider } from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import {
  useProfileFormSubmissionContext,
  useResetProfileFormSubmissionContextOnNavigateAway,
  useSetInProgressProfile,
  useSetPendingProfile,
} from '../../../../../../../../../features/registration/profile/form/profile-form-submission-context';
import {
  formValuesToRegistrationProfileType,
  getInitialFormValuesForRegistrationProfile,
  RegistrationProfileForm,
  type RegistrationProfileFormValues,
  registrationProfileFormSchema,
} from '../../../../../../../../../features/registration/profile/form/registration-profile-form';
import { RegistrationProfileFormTableOfContents } from '../../../../../../../../../features/registration/profile/form/registration-profile-form-table-of-contents';
import {
  getRegistrationProfileDetailPath,
  getRegistrationProfileEditPath,
} from '../../../../../../../../../features/registration/profile/registration-profile-routing';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';

const searchSchema = z.object({
  versionId: z.string().optional(),
});

interface WithDefaultValues {
  defaultValues: RegistrationProfileFormValues | undefined;
}

export const Route = createFileRoute('/_app/_clearing-core/registration/profile/_profile/$profileId/edit/_edit/')({
  validateSearch: searchSchema,
  loader: ({ params }) => {
    const { consumeInProgressProfile } = useProfileFormSubmissionContext.getState();

    const inProgressProfile = consumeInProgressProfile(undefined);

    const defaultValues = match(inProgressProfile)
      .with({ profileId: params.profileId, individual: P.not(P.nullish) }, ({ individual }) => individual)
      .with({ profileId: params.profileId, organization: P.not(P.nullish) }, ({ organization }) => organization)
      .otherwise(() => undefined);

    return {
      defaultValues,
    } as const satisfies WithDefaultValues;
  },
  component: EditProfile,
  preload: false,
  shouldReload: true,
});

const rightColumnContainer = {
  width: { type: 'fixed', width: 320 },
} as const;

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load profile', 'view this profile');
const loadProfileVersionErrorMessageGetter = buildDefaultErrorMessageGetter('Failed to load profile version', 'load this profile version');

function EditProfile() {
  const { profileId } = Route.useParams();
  const { versionId: explicitBasedOnVersionId } = Route.useSearch();
  const { defaultValues: contextDefaultValues } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const isTooCompactForTableOfContents = useBreakpoint('sm');
  const editProfileLink = getRegistrationProfileEditPath(profileId);

  useResetProfileFormSubmissionContextOnNavigateAway(editProfileLink);

  const setInProgressProfile = useSetInProgressProfile();
  const setPendingProfile = useSetPendingProfile();

  const { data: profileData, error } = useIxcRegistrationV1ProfileQueryServiceProfileGet({ profileId });
  useErrorHandler(error, errorMessageGetter);

  const basedOnVersionId =
    explicitBasedOnVersionId ??
    profileData?.profile?.data?.pendingVersion?.profileVersionId ??
    profileData?.profile?.data?.currentVersion?.profileVersionId;

  const { data: profileVersionData, error: profileVersionError } = useIxcRegistrationV1ProfileVersionQueryServiceProfileVersionGet(
    basedOnVersionId ? { profileVersionId: basedOnVersionId } : undefined,
  );
  useErrorHandler(profileVersionError, loadProfileVersionErrorMessageGetter);

  const defaultValues = useMemo(
    () => contextDefaultValues ?? getInitialFormValuesForRegistrationProfile(profileVersionData?.profileVersion?.data?.profile),
    [contextDefaultValues, profileVersionData?.profileVersion?.data?.profile],
  );

  const form = useForm<RegistrationProfileFormValues>({
    mode: 'all',
    defaultValues,
    resolver: zodResolver(registrationProfileFormSchema),
  });
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    reset,
  } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const lens = useLens({ control });
  const usableProgramId = profileData?.profile?.programId ?? profileVersionData?.profileVersion?.programId ?? '';
  const selectedProfileType = useWatch(lens.focus('type').interop());
  const onSubmit = useCallback(
    async (values: RegistrationProfileFormValues) => {
      setInProgressProfile(
        match(values)
          .with({ type: 'individual' }, (v) => ({ profileId, programId: usableProgramId, individual: v }))
          .with({ type: 'organization' }, (v) => ({ profileId, programId: usableProgramId, organization: v }))
          .exhaustive(),
      );

      setPendingProfile({ programId: usableProgramId, spec: formValuesToRegistrationProfileType(values) });

      await navigate({
        to: `/registration/profile/$profileId/edit/review`,
        params: { profileId },
      });
    },
    [setInProgressProfile, setPendingProfile, usableProgramId, navigate, profileId],
  );

  const baseFormElements = (
    <VerticalStack gap={7}>
      <RegistrationProfileForm lens={lens} programId={usableProgramId} />
    </VerticalStack>
  );

  const profileLink = getRegistrationProfileDetailPath(profileId);

  return (
    <TableOfContentsContextProvider>
      <FormProvider {...form}>
        <FormLayout
          actions={[
            ...(profileLink
              ? ([
                  {
                    buttonLink: {
                      id: 'cancel',
                      children: 'Cancel',
                      LinkElement: Link,
                      to: profileLink,
                      variant: 'secondary',
                    },
                  },
                ] as const)
              : []),
            {
              button: {
                id: 'continue',
                children: 'Continue',
                disabled: !isValid || (!contextDefaultValues && !isDirty),
                type: 'submit',
                variant: 'primary',
              },
            },
          ]}
          maxWidthContainer="1040px"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isTooCompactForTableOfContents ? (
            baseFormElements
          ) : (
            <WrappingLayoutContainer
              gap={DEFAULT_PAGE_LAYOUT_GAP}
              left={{
                ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
                render: baseFormElements,
              }}
              padding={LAYOUT_ZERO_CONTAINER_PADDING}
              right={{
                ...rightColumnContainer,
                render: (isWrapping) => (isWrapping ? null : <RegistrationProfileFormTableOfContents selectedProfileType={selectedProfileType} />),
              }}
              wrap="forward"
            />
          )}
        </FormLayout>
      </FormProvider>
    </TableOfContentsContextProvider>
  );
}
